import { objectSpread2 as _objectSpread2, toConsumableArray as _toConsumableArray, typeof as _typeof, defineProperty as _defineProperty } from '../_virtual/_rollupPluginBabelHelpers.js';
import elevationTransform from './elevation-transform.esm.js';
import hasDefault from './has-default.esm.js';
import lhCropTransform from './lh-crop-transform.esm.js';
import { THEME_UI_DEFAULT_KEY } from './types.esm.js';
import { emMarginTransform, emPaddingTransform } from '../styles-system/em-space.esm.js';
import { resolveMargin, getSpacing } from '../styles-system/space.esm.js';
import { getFontSize } from '../styles-system/typography.esm.js';
import { defaultBreakpoints } from '../themes/theme-keys/breakpoints.esm.js';
import { isNotEmptyObject } from '../utils/assertion.esm.js';
import { memoizedGet, EMPTY_OBJECT } from '../utils/object.esm.js';

var defaultTheme = {
  space: [0, 4, 8, 16, 32, 64, 128, 256, 512],
  fontSizes: [12, 14, 16, 20, 24, 32, 48, 64, 72]
};
var aliases = {
  bg: 'backgroundColor',
  m: 'margin',
  mt: 'marginTop',
  mr: 'marginRight',
  mb: 'marginBottom',
  ml: 'marginLeft',
  mx: 'marginX',
  my: 'marginY',
  p: 'padding',
  pt: 'paddingTop',
  pr: 'paddingRight',
  pb: 'paddingBottom',
  pl: 'paddingLeft',
  px: 'paddingX',
  py: 'paddingY'
};
var postAliases = {
  emPadding: 'padding',
  emPaddingTop: 'paddingTop',
  emPaddingRight: 'paddingRight',
  emPaddingBottom: 'paddingBottom',
  emPaddingLeft: 'paddingLeft',
  emPaddingX: 'paddingX',
  emPaddingY: 'paddingY',
  emMargin: 'margin',
  emMarginTop: 'marginTop',
  emMarginRight: 'marginRight',
  emMarginBottom: 'marginBottom',
  emMarginLeft: 'marginLeft',
  emMarginX: 'marginX',
  emMarginY: 'marginY',
  lhCrop: '',
  // empty string will apply the generated css object to the parent css object (fall back handling by emotion)
  elevation: 'boxShadow'
};
function resolveDefaultVariant(scaleKey, props) {
  return scaleKey === 'elevation' && 'boxShadow' in props ? props['boxShadow'] : undefined;
}
var multiples = {
  marginX: ['marginLeft', 'marginRight'],
  marginY: ['marginTop', 'marginBottom'],
  paddingX: ['paddingLeft', 'paddingRight'],
  paddingY: ['paddingTop', 'paddingBottom'],
  size: ['width', 'height']
};
var scales = {
  color: 'colors',
  backgroundColor: 'colors',
  borderColor: 'colors',
  caretColor: 'colors',
  columnRuleColor: 'colors',
  opacity: 'opacities',
  transition: 'transitions',
  margin: 'space',
  marginTop: 'space',
  marginRight: 'space',
  marginBottom: 'space',
  marginLeft: 'space',
  marginX: 'space',
  marginY: 'space',
  marginBlock: 'space',
  marginBlockEnd: 'space',
  marginBlockStart: 'space',
  marginInline: 'space',
  marginInlineEnd: 'space',
  marginInlineStart: 'space',
  padding: 'space',
  paddingTop: 'space',
  paddingRight: 'space',
  paddingBottom: 'space',
  paddingLeft: 'space',
  paddingX: 'space',
  paddingY: 'space',
  emMargin: 'emSpace',
  emMarginTop: 'emSpace',
  emMarginRight: 'emSpace',
  emMarginBottom: 'emSpace',
  emMarginLeft: 'emSpace',
  emMarginX: 'emSpace',
  emMarginY: 'emSpace',
  emPadding: 'emSpace',
  emPaddingTop: 'emSpace',
  emPaddingRight: 'emSpace',
  emPaddingBottom: 'emSpace',
  emPaddingLeft: 'emSpace',
  emPaddingX: 'emSpace',
  emPaddingY: 'emSpace',
  paddingBlock: 'space',
  paddingBlockEnd: 'space',
  paddingBlockStart: 'space',
  paddingInline: 'space',
  paddingInlineEnd: 'space',
  paddingInlineStart: 'space',
  scrollPadding: 'space',
  scrollPaddingTop: 'space',
  scrollPaddingRight: 'space',
  scrollPaddingBottom: 'space',
  scrollPaddingLeft: 'space',
  scrollPaddingX: 'space',
  scrollPaddingY: 'space',
  inset: 'space',
  insetBlock: 'space',
  insetBlockEnd: 'space',
  insetBlockStart: 'space',
  insetInline: 'space',
  insetInlineEnd: 'space',
  insetInlineStart: 'space',
  top: 'space',
  right: 'space',
  bottom: 'space',
  left: 'space',
  gridGap: 'space',
  gridColumnGap: 'space',
  gridRowGap: 'space',
  gap: 'space',
  columnGap: 'space',
  rowGap: 'space',
  fontFamily: 'fonts',
  fontSize: 'fontSizes',
  fontWeight: 'fontWeights',
  lineHeight: 'lineHeights',
  letterSpacing: 'letterSpacings',
  outline: 'borders',
  border: 'borders',
  borderTop: 'borders',
  borderRight: 'borders',
  borderBottom: 'borders',
  borderLeft: 'borders',
  borderWidth: 'borderWidths',
  borderStyle: 'borderStyles',
  borderRadius: 'radii',
  borderTopRightRadius: 'radii',
  borderTopLeftRadius: 'radii',
  borderBottomRightRadius: 'radii',
  borderBottomLeftRadius: 'radii',
  borderTopWidth: 'borderWidths',
  borderTopColor: 'colors',
  borderTopStyle: 'borderStyles',
  borderBottomWidth: 'borderWidths',
  borderBottomColor: 'colors',
  borderBottomStyle: 'borderStyles',
  borderLeftWidth: 'borderWidths',
  borderLeftColor: 'colors',
  borderLeftStyle: 'borderStyles',
  borderRightWidth: 'borderWidths',
  borderRightColor: 'colors',
  borderRightStyle: 'borderStyles',
  borderBlock: 'borders',
  borderBlockEnd: 'borders',
  borderBlockEndStyle: 'borderStyles',
  borderBlockEndWidth: 'borderWidths',
  borderBlockStart: 'borders',
  borderBlockStartStyle: 'borderStyles',
  borderBlockStartWidth: 'borderWidths',
  borderBlockStyle: 'borderStyles',
  borderBlockWidth: 'borderWidths',
  borderEndEndRadius: 'radii',
  borderEndStartRadius: 'radii',
  borderInline: 'borders',
  borderInlineEnd: 'borders',
  borderInlineEndStyle: 'borderStyles',
  borderInlineEndWidth: 'borderWidths',
  borderInlineStart: 'borders',
  borderInlineStartStyle: 'borderStyles',
  borderInlineStartWidth: 'borderWidths',
  borderInlineStyle: 'borderStyles',
  borderInlineWidth: 'borderWidths',
  borderStartEndRadius: 'radii',
  borderStartStartRadius: 'radii',
  outlineColor: 'colors',
  elevation: 'elevations',
  boxShadow: 'shadows',
  textShadow: 'shadows',
  zIndex: 'zIndices',
  width: 'sizes',
  minWidth: 'sizes',
  maxWidth: 'sizes',
  height: 'sizes',
  minHeight: 'sizes',
  maxHeight: 'sizes',
  flexBasis: 'sizes',
  size: 'sizes',
  blockSize: 'sizes',
  inlineSize: 'sizes',
  maxBlockSize: 'sizes',
  maxInlineSize: 'sizes',
  minBlockSize: 'sizes',
  minInlineSize: 'sizes',
  // svg
  fill: 'colors',
  stroke: 'colors',
  lhCrop: 'lhCrops'
};
var positiveOrNegative = function positiveOrNegative(value, scale) {
  if (typeof value !== 'number' || value >= 0) {
    if (typeof value === 'string' && value.startsWith('-')) {
      var valueWithoutMinus = value.substring(1);
      var _n = memoizedGet(scale, valueWithoutMinus, valueWithoutMinus);
      return "-".concat(_n);
    }
    return memoizedGet(scale, value, value);
  }
  var absolute = Math.abs(value);
  var n = memoizedGet(scale, absolute, absolute);
  if (typeof n === 'string') return '-' + n;
  return Number(n) * -1;
};
var marginTransforms = ['margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft', 'marginX', 'marginY', 'marginBlock', 'marginBlockEnd', 'marginBlockStart', 'marginInline', 'marginInlineEnd', 'marginInlineStart'].reduce(
// (acc, curr) => ({
//   ...acc,
//   [curr]: resolveMargin
// }),
function (acc, curr) {
  acc[curr] = resolveMargin;
  return acc;
}, {});
var emMarginTransforms = ['emMargin', 'emMarginTop', 'emMarginRight', 'emMarginBottom', 'emMarginLeft', 'emMarginX', 'emMarginY'].reduce(function (acc, curr) {
  return _objectSpread2(_objectSpread2({}, acc), {}, _defineProperty({}, curr, emMarginTransform));
}, {});
var tShirtSizeSpacingTransforms = ['padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'paddingX', 'paddingY', 'gap', 'gridGap', 'gridColumnGap', 'gridRowGap', 'columnGap', 'rowGap'].reduce(function (acc, curr) {
  return _objectSpread2(_objectSpread2({}, acc), {}, _defineProperty({}, curr, getSpacing));
}, {});
var emPaddingTransforms = ['emPadding', 'emPaddingTop', 'emPaddingRight', 'emPaddingBottom', 'emPaddingLeft', 'emPaddingX', 'emPaddingY'].reduce(function (acc, curr) {
  return _objectSpread2(_objectSpread2({}, acc), {}, _defineProperty({}, curr, emPaddingTransform));
}, {});
var fontSizeTransforms = ['fontSize'].reduce(function (acc, curr) {
  return _objectSpread2(_objectSpread2({}, acc), {}, _defineProperty({}, curr, getFontSize));
}, {});
var positionTransforms = ['top', 'bottom', 'left', 'right'].reduce(function (acc, curr) {
  return _objectSpread2(_objectSpread2({}, acc), {}, _defineProperty({}, curr, positiveOrNegative));
}, {});
var lhCropTransforms = ['lhCrop'].reduce(function (acc, curr) {
  return _objectSpread2(_objectSpread2({}, acc), {}, _defineProperty({}, curr, lhCropTransform));
}, {});
var elevationTransforms = ['elevation'].reduce(function (acc, curr) {
  return _objectSpread2(_objectSpread2({}, acc), {}, _defineProperty({}, curr, elevationTransform));
}, {});
var customTransforms = _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, marginTransforms), emMarginTransforms), tShirtSizeSpacingTransforms), emPaddingTransforms), fontSizeTransforms), positionTransforms), lhCropTransforms), elevationTransforms);
var customTransformKeys = Object.keys(customTransforms);
var responsive = function responsive(styles) {
  return function (theme) {
    var props = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var next = {};
    var breakpoints = theme ? memoizedGet(theme, 'breakpoints', defaultBreakpoints) : defaultBreakpoints;
    var mediaQueries = [null].concat(_toConsumableArray(breakpoints.map()));
    for (var k in styles) {
      var key = k;
      var value = styles[key];
      if (typeof value === 'function') {
        value = value(theme || {}, props);
      }
      if (value == null) continue;
      if (!Array.isArray(value)) {
        next[key] = value;
        continue;
      }
      for (var i = 0; i < value.slice(0, mediaQueries.length).length; i++) {
        var media = mediaQueries[i];
        if (!media) {
          next[key] = value[i];
          continue;
        }
        next[media] = next[media] || {};
        if (value[i] == null) continue;
        next[media][key] = value[i];
      }
    }
    return next;
  };
};
function getObjectWithVariants(obj, theme) {
  var props = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  if (obj && obj['variant']) {
    var result = {};
    for (var key in obj) {
      var x = obj[key];
      if (key === 'variant') {
        var val = typeof x === 'function' ? x(theme, props) : x;
        var variant = getObjectWithVariants(memoizedGet(theme, val), theme);
        result = _objectSpread2(_objectSpread2({}, result), variant);
      } else {
        result[key] = x;
      }
    }
    return result;
  }
  return obj;
}
function css() {
  var args = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var theme = _objectSpread2(_objectSpread2({}, defaultTheme), 'theme' in props ? props.theme : props);

    // insert variant props before responsive styles, so they can be merged
    // we need to maintain order of the style props, so if a variant is place in the middle
    // of other props, it will extends its props at that same location order.

    var obj = getObjectWithVariants(typeof args === 'function' ? args(theme, props) : args, theme, props);
    var styles = responsive(obj)(theme, props);
    var result = {};
    for (var key in styles) {
      var x = styles[key];
      var val = typeof x === 'function' ? x(theme, props) : x;
      if (val === undefined || val === null) {
        continue;
      }
      if (val && _typeof(val) === 'object') {
        if (hasDefault(val)) {
          result[key] = val[THEME_UI_DEFAULT_KEY];
          continue;
        }

        // On type level, val can also be an array here,
        // but we transform all arrays in `responsive` function.
        result[key] = css(val)(theme);
        continue;
      }
      var scaleKey = key in aliases ? aliases[key] : key;
      var scaleName = scaleKey in scales ? scales[scaleKey] : undefined;
      var scale = memoizedGet(theme, scaleName, memoizedGet(theme, scaleKey, EMPTY_OBJECT));
      var transform = memoizedGet(customTransforms, scaleKey, memoizedGet);
      var value = customTransformKeys.includes(scaleKey) ? transform(val, scale, theme, '__themeKey' in props && 'variant' in props ? "".concat(props.__themeKey, ".").concat(props.variant) : resolveDefaultVariant(scaleKey, obj)) : transform(scale, val, val);
      scaleKey = scaleKey in postAliases ? postAliases[scaleKey] : scaleKey;
      if (multiples[scaleKey]) {
        var dirs = multiples[scaleKey];
        for (var i = 0; i < dirs.length; i++) {
          result[dirs[i]] = value;
        }
      } else {
        result[scaleKey] = value;
      }
    }
    if (isNotEmptyObject(result)) ;
    return result;
  };
}

export default css;
export { css, responsive };
//# sourceMappingURL=css.esm.js.map
